import { useDispatch, useSelector } from "react-redux";
/**
 * ユーザー作成ウィザード
 *
 * ステップ形式でユーザー作成を行う:
 * 1. 名前入力
 * 2. アイコン選択方法
 * 3A. プリセットアイコン選択 / 3B. プロフィール入力 → AI生成
 * 4. プレビュー → 作成
 *
 * スタイルルール（IconSelector参考）:
 * - hover: 不使用（スマホ向け）
 * - transition: 不使用
 * - 選択ボタン: btnDelay不使用
 * - アクションボタン（戻る、次へ、確定）: btnDelay使用、親要素にalphaクラス
 * - フッター: 固定位置（h-[100px]）
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Dialog } from "@mui/material";
import { customAlphabet } from "nanoid";
import throttle from "lodash-es/throttle";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { getEnv } from "app/configSlice";
import { recordEvent } from "app/analytics";
import { AnalyticsEventType } from "app/types";
import { AppDispatch, RootState } from "app/store";
import I18n from "common/i18n";
import { btnDelay, useScale, usePlaySound } from "common/utils";
import { CloseBtn, SpinModal, FadeTransition } from "common/elements";
import { GenerationStatus } from "features/generation/types";
import { createChildAccount, createGuestAccount } from "features/user/api";
import { useAIImageGeneration } from "features/generation/hooks/useAIImageGeneration";
import { StepProgressBar } from "./components";
import { isValidBirthday } from "./steps/ProfileAIStep/BirthdayStep";
import {
  NameInputStep,
  IconMethodStep,
  PresetIconStep,
  UploadIconStep,
  ProfileAIStep,
  PreviewStep,
} from "./steps";
import {
  WizardStep,
  IconMethod,
  ProfileSubStep,
  FavoritesSubStep,
  CreateUserWizardProps,
  CreateUserState,
  WIZARD_STEPS,
  initialState,
  UserType,
  ChildGender,
} from "./types";

const PANEL_WIDTH = 1100;
const PANEL_HEIGHT = 830;

/** 年齢計算 */
const calculateAge = (birthday: string): number => {
  const birthDate = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

/**
 * プロフィールが完全かどうかを判定
 * 全項目が入力されているかチェック
 */
const isProfileComplete = (state: CreateUserState): boolean => {
  if (!state.gender || state.gender === ChildGender.NONE) return false;
  if (!isValidBirthday(state.birthday)) return false;
  if (!state.accessory || state.accessory.length === 0) return false;
  if (!state.hairstyle) return false;
  if (!state.bodyType) return false;
  if (!state.faceShape) return false;
  if (!state.favoriteColors || state.favoriteColors.length === 0) return false;
  if (!state.bestSubjects || state.bestSubjects.length === 0) return false;
  return true;
};

/**
 * 各ステップが入力済みかどうかを判定
 * ProfileSubStepBarで入力済みステップにチェックを表示するために使用
 */
const getCompletedStepIds = (state: CreateUserState): string[] => {
  const completedIds: string[] = [];

  // gender
  if (state.gender && state.gender !== ChildGender.NONE) {
    completedIds.push("gender");
  }

  // birthday（有効な日付）
  if (isValidBirthday(state.birthday)) {
    completedIds.push("birthday");
  }

  // appearance (accessory, hairstyle, bodyType, faceShape の4項目すべてが入力されていれば完了)
  if (
    state.accessory &&
    state.accessory.length > 0 &&
    state.hairstyle &&
    state.bodyType &&
    state.faceShape
  ) {
    completedIds.push("appearance");
  }

  // color (favoriteColors が入力されていれば完了)
  if (state.favoriteColors && state.favoriteColors.length > 0) {
    completedIds.push("color");
  }

  // bestSubject (bestSubjects が入力されていれば完了)
  if (state.bestSubjects && state.bestSubjects.length > 0) {
    completedIds.push("bestSubject");
  }

  return completedIds;
};

export const CreateUserWizard = ({ open, close, userType, onSuccess }: CreateUserWizardProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch<AppDispatch>();
  const parent = useSelector((state: RootState) => state.user.parent);

  const [currentStep, setCurrentStep] = useState<WizardStep>(WizardStep.NAME_INPUT);
  const [profileSubStep, setProfileSubStep] = useState<ProfileSubStep>(ProfileSubStep.GENDER);
  const [state, setState] = useState<CreateUserState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // FavoritesStep 用の状態（子供向けステップ形式）
  const [favoritesSubStep, setFavoritesSubStep] = useState<FavoritesSubStep>(
    FavoritesSubStep.COLOR,
  );

  // 追加プロンプト（再生成時のユーザー入力）
  const [additionalPrompt, setAdditionalPrompt] = useState<string>("");

  // AI画像生成hook
  const {
    status: generationStatus,
    progress: generationProgress,
    partialImage,
    results: generationResults,
    generate,
    cancel: cancelGeneration,
    reset: resetGeneration,
  } = useAIImageGeneration({}); // プロフィール用はUIDなしで生成

  // 状態更新
  const updateState = useCallback((updates: Partial<CreateUserState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // 生成結果をstateに反映
  useEffect(() => {
    if (generationResults.length > 0 && generationStatus === GenerationStatus.COMPLETE) {
      updateState({ icon: generationResults[0].url });
    }
  }, [generationResults, generationStatus, updateState]);

  // ダイアログを開いた時に初期化
  useEffect(() => {
    if (open) {
      const customNanoid = customAlphabet("1234567890", 7);
      const random = customNanoid();
      setState({
        ...initialState,
        name: I18n.t("MSG_USER_PROFILE_DEFAULT_NAME", { random }),
      });
      setCurrentStep(WizardStep.NAME_INPUT);
      setProfileSubStep(ProfileSubStep.GENDER);
      setIsSubmitting(false);
      setAdditionalPrompt("");
      resetGeneration();
      // FavoritesStep 状態をリセット（appearance から開始）
      setFavoritesSubStep(FavoritesSubStep.APPEARANCE);
    }
  }, [open, resetGeneration]);

  // ステップ遷移
  const goToStep = useCallback((step: WizardStep) => {
    setCurrentStep(step);
  }, []);

  // 次へボタンが有効かどうか
  const canProceed = useCallback(() => {
    switch (currentStep) {
      case WizardStep.NAME_INPUT:
        return state.name.trim().length > 0 && state.name.length <= 10;
      case WizardStep.ICON_METHOD:
        return false; // IconMethodStepで直接遷移
      case WizardStep.PRESET_ICON:
        return !!state.icon;
      case WizardStep.UPLOAD_ICON:
        return !!state.icon;
      case WizardStep.PROFILE_AI:
        // サブステップに応じた判定
        switch (profileSubStep) {
          case ProfileSubStep.GENDER:
            return !!state.gender && state.gender !== ChildGender.NONE;
          case ProfileSubStep.BIRTHDAY:
            return (
              state.birthdayYear !== null &&
              state.birthdayMonth !== null &&
              state.birthdayDay !== null &&
              isValidBirthday(state.birthday)
            );
          case ProfileSubStep.FAVORITES:
            // appearanceサブステップは全項目必須
            if (favoritesSubStep === FavoritesSubStep.APPEARANCE) {
              return (
                state.accessory.length > 0 &&
                !!state.hairstyle &&
                !!state.bodyType &&
                !!state.faceShape
              );
            }
            if (favoritesSubStep === FavoritesSubStep.COLOR) {
              return state.favoriteColors.length > 0;
            }
            if (favoritesSubStep === FavoritesSubStep.BEST_SUBJECT) {
              return isProfileComplete(state);
            }
            return false;
          case ProfileSubStep.ICON_PREVIEW:
            return generationStatus === GenerationStatus.COMPLETE && !!state.icon;
          default:
            return false;
        }
      case WizardStep.PREVIEW:
        return true;
      default:
        return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    profileSubStep,
    favoritesSubStep,
    state.name,
    state.icon,
    state.gender,
    state.birthday,
    state.accessory,
    state.hairstyle,
    state.bodyType,
    state.faceShape,
    state.favoriteColors,
    state.bestSubjects,
    generationStatus,
  ]);

  // 次へ
  const handleNext = useCallback(() => {
    play();
    btnDelay(() => {
      switch (currentStep) {
        case WizardStep.NAME_INPUT:
          setCurrentStep(WizardStep.ICON_METHOD);
          break;
        case WizardStep.PRESET_ICON:
          setCurrentStep(WizardStep.PREVIEW);
          break;
        case WizardStep.UPLOAD_ICON:
          setCurrentStep(WizardStep.PREVIEW);
          break;
        case WizardStep.PROFILE_AI:
          // サブステップの遷移（userTypeによって分岐）
          switch (profileSubStep) {
            case ProfileSubStep.GENDER:
              setProfileSubStep(ProfileSubStep.BIRTHDAY);
              break;
            case ProfileSubStep.BIRTHDAY:
              setProfileSubStep(ProfileSubStep.FAVORITES);
              setFavoritesSubStep(FavoritesSubStep.APPEARANCE);
              break;
            case ProfileSubStep.FAVORITES:
              // appearance → color → bestSubject → 生成開始
              if (favoritesSubStep === FavoritesSubStep.APPEARANCE) {
                setFavoritesSubStep(FavoritesSubStep.COLOR);
              } else if (favoritesSubStep === FavoritesSubStep.COLOR) {
                setFavoritesSubStep(FavoritesSubStep.BEST_SUBJECT);
              }
              // bestSubject → 生成開始はhandleStartGenerationで行う
              break;
            case ProfileSubStep.ICON_PREVIEW:
              // 生成完了後、確認ページへ
              setCurrentStep(WizardStep.PREVIEW);
              break;
            default:
              break;
          }
          break;
        default:
          break;
      }
    });
  }, [currentStep, profileSubStep, favoritesSubStep, play]);

  // 戻る
  const handleBack = useCallback(() => {
    play();
    btnDelay(() => {
      switch (currentStep) {
        case WizardStep.ICON_METHOD:
          setCurrentStep(WizardStep.NAME_INPUT);
          break;
        case WizardStep.PRESET_ICON:
          setCurrentStep(WizardStep.ICON_METHOD);
          break;
        case WizardStep.UPLOAD_ICON:
          setCurrentStep(WizardStep.ICON_METHOD);
          break;
        case WizardStep.PROFILE_AI:
          // サブステップの戻る（userTypeによって分岐）
          switch (profileSubStep) {
            case ProfileSubStep.GENDER:
              setCurrentStep(WizardStep.ICON_METHOD);
              setProfileSubStep(ProfileSubStep.GENDER);
              break;
            case ProfileSubStep.BIRTHDAY:
              setProfileSubStep(ProfileSubStep.GENDER);
              break;
            case ProfileSubStep.FAVORITES:
              // bestSubject → color → appearance → BIRTHDAY
              if (favoritesSubStep === FavoritesSubStep.BEST_SUBJECT) {
                setFavoritesSubStep(FavoritesSubStep.COLOR);
              } else if (favoritesSubStep === FavoritesSubStep.COLOR) {
                setFavoritesSubStep(FavoritesSubStep.APPEARANCE);
              } else {
                // appearance → BIRTHDAYに戻る
                setProfileSubStep(ProfileSubStep.BIRTHDAY);
              }
              break;
            case ProfileSubStep.ICON_PREVIEW:
              // FAVORITES（bestSubject）に戻る
              setProfileSubStep(ProfileSubStep.FAVORITES);
              setFavoritesSubStep(FavoritesSubStep.BEST_SUBJECT);
              resetGeneration();
              break;
            default:
              break;
          }
          break;
        case WizardStep.PREVIEW:
          if (state.iconMethod === IconMethod.PRESET) {
            setCurrentStep(WizardStep.PRESET_ICON);
          } else if (state.iconMethod === IconMethod.UPLOAD) {
            setCurrentStep(WizardStep.UPLOAD_ICON);
          } else {
            setCurrentStep(WizardStep.PROFILE_AI);
            setProfileSubStep(ProfileSubStep.ICON_PREVIEW);
          }
          break;
        default:
          break;
      }
    });
  }, [currentStep, profileSubStep, favoritesSubStep, state.iconMethod, play, resetGeneration]);

  // AI生成開始
  const handleStartGeneration = useCallback(() => {
    play();
    btnDelay(() => {
      setProfileSubStep(ProfileSubStep.ICON_PREVIEW);
      // プロフィール情報をそのままAPIに渡す（LLMが判断）
      const age = state.birthday ? calculateAge(state.birthday) : undefined;
      generate({
        gender: (state.gender as "male" | "female" | "none") || "none",
        age,
        favorite_colors: state.favoriteColors?.length > 0 ? state.favoriteColors : undefined,
        best_subjects: state.bestSubjects?.length > 0 ? state.bestSubjects : undefined,
        accessory: state.accessory?.length > 0 ? state.accessory : undefined,
        hairstyle: state.hairstyle || undefined,
        body_type: state.bodyType || undefined,
        face_shape: state.faceShape || undefined,
      });
    });
  }, [play, generate, state]);

  // 再生成
  const _handleRegenerate = useCallback(() => {
    play();
    btnDelay(() => {
      resetGeneration();
      updateState({ icon: "" });
      // プロフィール情報をそのままAPIに渡す（LLMが判断）
      const age = state.birthday ? calculateAge(state.birthday) : undefined;
      generate({
        gender: (state.gender as "male" | "female" | "none") || "none",
        age,
        favorite_colors: state.favoriteColors?.length > 0 ? state.favoriteColors : undefined,
        best_subjects: state.bestSubjects?.length > 0 ? state.bestSubjects : undefined,
        accessory: state.accessory?.length > 0 ? state.accessory : undefined,
        hairstyle: state.hairstyle || undefined,
        body_type: state.bodyType || undefined,
        face_shape: state.faceShape || undefined,
      });
    });
  }, [play, generate, resetGeneration, updateState, state]);

  // 追加プロンプト付き再生成（IconPreviewStepから呼ばれる）
  const handleRegenerateWithPrompt = useCallback(() => {
    play();
    btnDelay(() => {
      resetGeneration();
      updateState({ icon: "" });
      const age = state.birthday ? calculateAge(state.birthday) : undefined;
      generate({
        gender: (state.gender as "male" | "female" | "none") || "none",
        age,
        favorite_colors: state.favoriteColors?.length > 0 ? state.favoriteColors : undefined,
        best_subjects: state.bestSubjects?.length > 0 ? state.bestSubjects : undefined,
        accessory: state.accessory?.length > 0 ? state.accessory : undefined,
        hairstyle: state.hairstyle || undefined,
        body_type: state.bodyType || undefined,
        face_shape: state.faceShape || undefined,
        additional_prompt: additionalPrompt || undefined,
      });
    });
  }, [play, generate, resetGeneration, updateState, state, additionalPrompt]);

  // 追加プロンプト変更ハンドラー
  const handleAdditionalPromptChange = useCallback((prompt: string) => {
    setAdditionalPrompt(prompt);
  }, []);

  // 生成中止
  const handleCancelGeneration = useCallback(() => {
    play();
    btnDelay(() => {
      cancelGeneration();
      // FAVORITES（bestSubject）に戻る
      setProfileSubStep(ProfileSubStep.FAVORITES);
      setFavoritesSubStep(FavoritesSubStep.BEST_SUBJECT);
    });
  }, [play, cancelGeneration]);

  // ユーザー作成確定（throttle で連続クリック防止）
  const handleConfirm = useMemo(
    () =>
      throttle(
        async () => {
          if (isSubmitting) return; // ガード条件
          setIsSubmitting(true); // 即座にSpinModal表示
          play();
          btnDelay(async () => {
            try {
              // アイコンのメタ情報
              const iconMetadata =
                state.iconMethod === IconMethod.PROFILE_AI && state.icon
                  ? {
                      source: "ai_generated" as const,
                      file_size: generationResults[0]?.fileSize || 0,
                    }
                  : state.iconMethod === IconMethod.UPLOAD && state.iconMetadata
                    ? state.iconMetadata
                    : undefined;

              if (userType === UserType.GUEST) {
                // 体験ユーザー作成（プロフィール情報も含めて1回で完了）
                const response = await createGuestAccount({
                  user: {
                    icon: state.icon || undefined,
                    display_name: state.name,
                    gender: state.gender || undefined,
                    locale: I18n.locale,
                    env: getEnv(),
                    // プロフィール情報
                    birthday: state.birthday || undefined,
                    favorite_color:
                      state.favoriteColors?.length > 0 ? state.favoriteColors : undefined,
                    best_subject: state.bestSubjects?.length > 0 ? state.bestSubjects : undefined,
                    accessory: state.accessory?.length > 0 ? state.accessory : undefined,
                    hairstyle: state.hairstyle || undefined,
                    body_type: state.bodyType || undefined,
                    face_shape: state.faceShape || undefined,
                    // AI生成アイコンのメタ情報
                    icon_metadata: iconMetadata,
                  },
                });
                recordEvent(AnalyticsEventType.CHILD_CREATE, { is_guest: 1 });
                const auth = getAuth();
                await signInWithCustomToken(auth, response.data.idToken);
                close();
              } else {
                // 子供ユーザー作成（プロフィール情報も含めて1回で完了）
                const result = await dispatch(
                  createChildAccount({
                    user: {
                      parent_uid: parent!.uid,
                      display_name: state.name,
                      locale: I18n.locale,
                      env: getEnv(),
                      // プロフィール情報
                      icon: state.icon || undefined,
                      birthday: state.birthday || undefined,
                      gender: state.gender || undefined,
                      favorite_color:
                        state.favoriteColors?.length > 0 ? state.favoriteColors : undefined,
                      best_subject: state.bestSubjects?.length > 0 ? state.bestSubjects : undefined,
                      accessory: state.accessory?.length > 0 ? state.accessory : undefined,
                      hairstyle: state.hairstyle || undefined,
                      body_type: state.bodyType || undefined,
                      face_shape: state.faceShape || undefined,
                      // AI生成アイコンのメタ情報
                      icon_metadata: iconMetadata,
                    },
                  }),
                ).unwrap();
                recordEvent(AnalyticsEventType.CHILD_CREATE, { is_guest: 0 });

                if (onSuccess) {
                  onSuccess(result);
                } else {
                  close();
                }
              }
            } catch (error) {
              console.error("Failed to create user:", error);
              setIsSubmitting(false); // 失敗時のみリセット（リトライ可能に）
            }
          });
        },
        3000, // 3秒間は再実行を防止
        { leading: true, trailing: false },
      ),
    [state, userType, parent, dispatch, close, onSuccess, play, generationResults, isSubmitting],
  );

  // キャンセル
  const handleCancel = useCallback(() => {
    play();
    btnDelay(() => {
      close();
    });
  }, [close, play]);

  // サブステップクリック時の処理（完了済みステップに戻る）
  const handleSubStepClick = useCallback(
    (targetSubStep: ProfileSubStep, targetFavoritesSubStep?: FavoritesSubStep) => {
      // 生成中はクリック無効
      if (generationStatus === GenerationStatus.GENERATING) {
        return;
      }
      play();
      setProfileSubStep(targetSubStep);
      if (targetFavoritesSubStep) {
        setFavoritesSubStep(targetFavoritesSubStep);
      }
    },
    [play, generationStatus],
  );

  // 常に3ステップを表示（メインステップバー）
  const steps = WIZARD_STEPS;

  // ステップに応じたコンテンツをレンダリング
  const renderContent = () => {
    const stepProps = { state, updateState, userType };

    switch (currentStep) {
      case WizardStep.NAME_INPUT:
        return <NameInputStep {...stepProps} />;
      case WizardStep.ICON_METHOD:
        return <IconMethodStep {...stepProps} goToStep={goToStep} />;
      case WizardStep.PRESET_ICON:
        return <PresetIconStep {...stepProps} />;
      case WizardStep.UPLOAD_ICON:
        return <UploadIconStep {...stepProps} />;
      case WizardStep.PROFILE_AI:
        return (
          <ProfileAIStep
            state={state}
            updateState={updateState}
            userType={userType}
            currentSubStep={profileSubStep}
            generationStatus={generationStatus}
            generationProgress={generationProgress}
            generatedImage={state.icon || null}
            partialImage={partialImage}
            favoritesSubStep={favoritesSubStep}
            onSubStepNext={handleNext}
            onSubStepClick={handleSubStepClick}
            completedStepIds={getCompletedStepIds(state)}
            additionalPrompt={additionalPrompt}
            onAdditionalPromptChange={handleAdditionalPromptChange}
            onRegenerate={handleRegenerateWithPrompt}
          />
        );
      case WizardStep.PREVIEW:
        return <PreviewStep state={state} updateState={updateState} userType={userType} />;
      default:
        return null;
    }
  };

  // フッター（固定位置）
  const renderFooter = () => {
    const isProfileAIStep = currentStep === WizardStep.PROFILE_AI;
    const isIconPreviewSubStep = isProfileAIStep && profileSubStep === ProfileSubStep.ICON_PREVIEW;
    const isGenerating = generationStatus === GenerationStatus.GENERATING;

    // 生成中は中止ボタンのみ
    if (isIconPreviewSubStep && isGenerating) {
      return (
        <div className="h-[80px] shrink-0 flex-row-el flex-center gap-4 alpha">
          <button onClick={handleCancelGeneration} className="btn btn-danger">
            <div className="flex-row-center gap-1 mb-[3px]">
              <span className="material-symbols-outlined !text-4xl text-white">close</span>
              <p className="text-body text-white text-font-size-body1">
                {I18n.t("MSG_CANCEL_BTN")}
              </p>
            </div>
          </button>
        </div>
      );
    }

    // 生成完了時は2ボタン（もどる、これでいい！）
    if (isIconPreviewSubStep && !isGenerating) {
      const nextDisabled = !canProceed() || isSubmitting;
      return (
        <div className="h-[80px] shrink-0 flex-row-el flex-center gap-4 alpha">
          {/* もどる */}
          <button onClick={handleBack} className="btn btn-blue">
            <div className="flex-row-center gap-1 mb-[3px]">
              <span className="material-symbols-outlined !text-4xl text-white">arrow_back</span>
              <p className="text-body text-white text-font-size-body1">{I18n.t("MSG_BACK_BTN")}</p>
            </div>
          </button>
          {/* これでいい！ */}
          <button
            onClick={handleNext}
            disabled={nextDisabled}
            className={`btn ${nextDisabled ? "btn-disabled" : "btn-primary"}`}
          >
            <div className="flex-row-center gap-1 mb-[3px]">
              <span className="material-symbols-outlined !text-4xl text-white">check</span>
              <p className="text-body text-white text-font-size-body1">
                {I18n.t("MSG_ICON_CONFIRM_BTN")}
              </p>
            </div>
          </button>
        </div>
      );
    }

    const isConfirmStep = currentStep === WizardStep.PREVIEW;
    // FAVORITES（bestSubjectサブステップ）= 生成前の最後のサブステップ
    const isPreGenerateSubStep =
      isProfileAIStep &&
      profileSubStep === ProfileSubStep.FAVORITES &&
      favoritesSubStep === FavoritesSubStep.BEST_SUBJECT;

    // 戻るボタン表示条件
    const showBackButton = currentStep !== WizardStep.NAME_INPUT;

    // 次へ/確定ボタン表示条件（IconMethodのみ非表示）
    const showNextButton = currentStep !== WizardStep.ICON_METHOD;

    const nextDisabled = !canProceed() || isSubmitting;

    // ボタンラベルとアイコン
    let nextLabel = I18n.t("MSG_CREATE_USER_BTN_NEXT");
    let nextIcon = "arrow_forward";
    let onNextClick = handleNext;
    const backLabel = I18n.t("MSG_BACK_BTN");
    const onBackClick = handleBack;

    if (isConfirmStep) {
      nextLabel = I18n.t("MSG_CONFIRM_BTN");
      nextIcon = "check";
      onNextClick = handleConfirm;
    } else if (isPreGenerateSubStep) {
      nextLabel = I18n.t("MSG_CREATE_USER_BTN_GENERATE");
      nextIcon = "auto_awesome";
      onNextClick = handleStartGeneration;
    }

    return (
      <div className="h-[80px] shrink-0 flex-row-el flex-center gap-4 alpha">
        {showBackButton && (
          <button onClick={onBackClick} className="btn btn-blue">
            <div className="flex-row-center gap-1 mb-[3px]">
              <span className="material-symbols-outlined !text-4xl text-white">arrow_back</span>
              <p className="text-body text-white text-font-size-body1">{backLabel}</p>
            </div>
          </button>
        )}
        {showNextButton && (
          <button
            onClick={onNextClick}
            disabled={nextDisabled}
            className={`btn ${nextDisabled ? "btn-disabled" : "btn-primary"}`}
          >
            <div className="flex-row-center gap-1 mb-[3px]">
              <span className="material-symbols-outlined !text-4xl text-white">{nextIcon}</span>
              <p className="text-body text-white text-font-size-body1">{nextLabel}</p>
            </div>
          </button>
        )}
      </div>
    );
  };

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 999999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{
        style: {
          margin: 0,
          overflow: "visible",
          backgroundColor: "transparent",
        },
        elevation: 0,
      }}
    >
      <div className="flex-col-el flex-center absolute inset-0 bg-textcolor-black/50 w-full h-full">
        {isSubmitting && <SpinModal />}

        <div
          className="flex-col-el flex-center relative"
          style={{
            width: PANEL_WIDTH * scale,
            height: PANEL_HEIGHT * scale,
          }}
        >
          <div
            className="flex-col-el shrink-0 border-solid border-white border-[8px] rounded-[8px] bg-beige-60 relative p-6"
            style={{
              width: PANEL_WIDTH,
              height: PANEL_HEIGHT,
              transform: `scale(${scale})`,
              filter:
                "drop-shadow(0px 6px 6px rgba(0, 0, 0, 0.2)) drop-shadow(0px 10px 14px rgba(0, 0, 0, 0.14))",
            }}
          >
            <CloseBtn close={handleCancel} />

            {/* ステップインジケータ */}
            <StepProgressBar steps={steps} currentStep={currentStep} onStepClick={goToStep} />

            {/* コンテンツエリア */}
            <div className="flex-col-el flex-1 w-full overflow-hidden">{renderContent()}</div>

            {/* フッター（固定位置） */}
            {renderFooter()}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
